// src/features/accounts/hooks/useCreateAccount.js
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchCompaniesNames, fetchGroupsByCompany, createAccount, importAccountsFromExcel } from "../../../api/requests/account-requests";
import { isSoleProprietorship } from "../../../constants/accountTypes";
import { DISTRICTS } from "../../../constants/districts";

export const useCreateAccount = () => {
    const navigate = useNavigate();

    // -- State --
    const [companies, setCompanies] = useState([]);
    const [groups, setGroups] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);

    const [loadingInit, setLoadingInit] = useState(true);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);

    const [error, setError] = useState(null);

    const [form, setForm] = useState({
        accountName: "",
        accountCode: "",
        title: "",
        taxOffice: "",
        nationalId: "",
        vknNumber: "",
        phoneNumber: "",
        country: "Türkiye",
        city: "",
        district: "",
        address: "",
        description: "",
        accountType: "SOLE_PROPRIETORSHIP",
        companyId: "",
        accountGroupId: "",
        enabled: true,
    });

    // -- Helpers --
    const showToast = (type, msg) => toast[type](msg, { autoClose: 3000 });

    const toTitleCase = (str) => {
        if (!str) return "";
        const lower = str.toLocaleLowerCase("tr-TR");
        return lower.replace(/(^|\s|\.)(\p{L})/gu, (m, b, l) => b + l.toLocaleUpperCase("tr-TR"));
    };

    const generateAccountCode = () => {
        const randomCode = Math.floor(100000 + Math.random() * 900000).toString();
        setForm((prev) => ({ ...prev, accountCode: randomCode }));
    };

    const validateTcKimlik = (tcKimlik) => {
        if (!tcKimlik || tcKimlik.length !== 11) return false;
        if (!/^\d{11}$/.test(tcKimlik)) return false;
        if (tcKimlik[0] === "0") return false;
        const d = tcKimlik.split("").map(Number);
        const sum1 = d[0] + d[2] + d[4] + d[6] + d[8];
        const sum2 = d[1] + d[3] + d[5] + d[7];
        const check1 = (sum1 * 7 - sum2) % 10;
        const check2 = (sum1 + sum2 + d[9]) % 10;
        return check1 === d[9] && check2 === d[10];
    };

    // -- GÜNCELLENMİŞ VALIDASYON --
    const validateForm = () => {
        if (!form.accountName.trim()) return "Cari/Bayi adı gereklidir";
        if (!form.accountCode.trim()) return "Cari/Bayi kodu gereklidir";
        if (form.accountCode.length !== 6) return "Cari/Bayi kodu 6 haneli olmalıdır";
        if (!form.companyId) return "Firma seçimi gereklidir";

        const isSoleProp = isSoleProprietorship(form.accountType);

        if (isSoleProp) {
            // Şahıs şirketi ise TC No ZORUNLU
            if (!form.nationalId) return "Şahıs şirketi için TC Kimlik numarası gereklidir";
            if (!validateTcKimlik(form.nationalId)) return "Geçerli bir TC Kimlik numarası giriniz";
        } else {
            // Tüzel ise VKN ZORUNLU
            if (!form.vknNumber) return "Bu cari/bayi tipi için VKN gereklidir";
            if (!/^\d{10}$/.test(form.vknNumber)) return "VKN 10 haneli olmalıdır";
        }

        // Global check (Extra güvenlik için tekrar kontrol)
        if (form.nationalId && !validateTcKimlik(form.nationalId)) {
            return "Geçerli bir TC Kimlik numarası giriniz";
        }
        if (form.vknNumber && !/^\d{10}$/.test(form.vknNumber)) {
            return "VKN 10 haneli olmalıdır";
        }

        if (form.phoneNumber && !/^[+]?[0-9]{7,15}$/.test(form.phoneNumber)) {
            return "Geçerli bir telefon numarası giriniz";
        }

        if (!form.country.trim()) return "Ülke bilgisi gereklidir";
        if (!form.city.trim()) return "Şehir seçimi gereklidir";
        if (!form.district.trim()) return "İlçe bilgisi gereklidir";
        if (!form.address.trim()) return "Adres bilgisi gereklidir";

        return null;
    };

    // -- Effects --
    useEffect(() => {
        const init = async () => {
            setLoadingInit(true);
            try {
                const res = await fetchCompaniesNames();
                setCompanies(res.data || []);
                if (!form.accountCode) generateAccountCode();
            } catch (err) {
                console.error(err);
                showToast("error", "Firmalar yüklenemedi");
            } finally {
                setLoadingInit(false);
            }
        };
        init();
    }, []);

    useEffect(() => {
        if (form.companyId) {
            const loadGroups = async () => {
                setLoadingGroups(true);
                try {
                    const res = await fetchGroupsByCompany(form.companyId);
                    setGroups(res.data || []);
                } catch {
                    setGroups([]);
                } finally {
                    setLoadingGroups(false);
                }
            };
            loadGroups();
        } else {
            setGroups([]);
            setForm((prev) => ({ ...prev, accountGroupId: "" }));
        }
    }, [form.companyId]);

    // -- Handlers --
    const handleAccountNameChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: toTitleCase(value) }));
        setError(null);
    };

    const handleAccountCodeChange = (e) => {
        const v = e.target.value.replace(/\D/g, "").slice(0, 6);
        setForm(prev => ({ ...prev, accountCode: v }));
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setError(null);

        if (type === "checkbox") {
            setForm((prev) => ({ ...prev, [name]: checked }));
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
            if (name === "city") {
                setDistrictOptions(DISTRICTS[value] || []);
                setForm((prev) => ({ ...prev, district: "" }));
            }
            if (name === "accountType") {
                // Tip değişince ID'leri sıfırla
                setForm((prev) => ({ ...prev, nationalId: "", vknNumber: "" }));
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            setSaving(false);
            return;
        }

        // Payload hazırlığı (Boş stringleri undefined yapmak isteyebilirsin,
        // ancak backend handle ediyorsa direkt form gönderilebilir)
        const payload = {
            ...form,
            accountName: form.accountName.trim(),
            accountCode: form.accountCode.trim(),
            title: form.title.trim() || undefined,
            taxOffice: form.taxOffice.trim() || undefined,
            nationalId: form.nationalId.trim() || undefined,
            vknNumber: form.vknNumber.trim() || undefined,
            phoneNumber: form.phoneNumber.trim() || undefined,
            country: form.country.trim() || undefined,
            city: form.city.trim() || undefined,
            district: form.district.trim() || undefined,
            address: form.address.trim() || undefined,
            description: form.description.trim() || undefined,
            accountGroupId: form.accountGroupId || undefined,
        };

        try {
            const res = await createAccount(payload);
            showToast("success", "Cari/Bayi başarıyla oluşturuldu!");
            const newId = res?.data?.id || res?.data?.data?.id; // API dönüş yapısına göre
            setTimeout(() => {
                navigate(newId ? `/accounts/${newId}` : `/accounts`);
            }, 1000);
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || "Oluşturma hatası";
            setError(errorMsg);
            showToast("error", errorMsg);
        } finally {
            setSaving(false);
        }
    };

    const handleImportExcel = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setImporting(true);
        try {
            const res = await importAccountsFromExcel(file);
            showToast("success", res.message || "Excel import işlemi başarılı!");
            setTimeout(() => navigate('/accounts'), 1500);
        } catch (err) {
            showToast("error", err.message || "Import sırasında hata oluştu");
        } finally {
            setImporting(false);
            e.target.value = null;
        }
    };

    return {
        form,
        companies,
        groups,
        districtOptions,
        loadingInit,
        loadingCompanies,
        loadingGroups,
        saving,
        importing,
        error,
        isSoleProp: isSoleProprietorship(form.accountType),

        // Actions
        handleAccountNameChange,
        handleAccountCodeChange,
        handleChange,
        generateAccountCode,
        handleSubmit,
        handleImportExcel,
        setForm
    };
};